import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Phone, MessageCircle, Instagram, Mail, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "385993509595";
const PHONE_DISPLAY = "+385 99 350 9595";
const INSTAGRAM_URL = "https://instagram.com/valeria_jacek";

export const ContactSection = ({ t, lang }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "",
    event_date: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", phone: "", event_type: "", event_date: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="section-pad relative bg-navy-deep">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionLabel eyebrow={t.contact.eyebrow} />
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-cream max-w-3xl leading-[1.05]">
          {t.contact.heading}
        </h2>
        <p className="text-cream/70 mt-6 max-w-2xl">{t.contact.desc}</p>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 mt-16">
          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-3 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t.contact.form.name} required>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-name-input"
                  className={inputCls}
                  placeholder="Ana Anić"
                />
              </Field>
              <Field label={t.contact.form.email} required>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-email-input"
                  className={inputCls}
                  placeholder="ana@email.com"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t.contact.form.phone}>
                <input
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="contact-phone-input"
                  className={inputCls}
                  placeholder="+385 ..."
                />
              </Field>
              <Field label={t.contact.form.event_date}>
                <input
                  type="date"
                  value={form.event_date}
                  onChange={update("event_date")}
                  data-testid="contact-date-input"
                  className={inputCls}
                />
              </Field>
            </div>

            <Field label={t.contact.form.event_type}>
              <input
                value={form.event_type}
                onChange={update("event_type")}
                data-testid="contact-eventtype-input"
                className={inputCls}
                placeholder={t.contact.form.placeholder_event}
              />
            </Field>

            <Field label={t.contact.form.message} required>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={update("message")}
                data-testid="contact-message-input"
                className={`${inputCls} resize-none`}
                placeholder={t.contact.form.placeholder_message}
              />
            </Field>

            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group inline-flex items-center gap-3 bg-gold text-navy px-8 py-4 font-medium tracking-[0.2em] uppercase text-sm hover:bg-gold-soft transition-all duration-300 disabled:opacity-60"
            >
              {sending ? t.contact.form.sending : t.contact.form.submit}
              <Send size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </form>

          {/* Direct contact panel */}
          <div className="lg:col-span-2 space-y-6">
            <DirectLink
              testId="contact-whatsapp-link"
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              icon={<MessageCircle size={20} />}
              label={t.contact.direct.whatsapp}
              value={PHONE_DISPLAY}
            />
            <DirectLink
              testId="contact-phone-link"
              href={`tel:+${WHATSAPP_NUMBER}`}
              icon={<Phone size={20} />}
              label={t.contact.direct.phone}
              value={PHONE_DISPLAY}
            />
            <DirectLink
              testId="contact-instagram-link"
              href={INSTAGRAM_URL}
              icon={<Instagram size={20} />}
              label={t.contact.direct.instagram}
              value={t.contact.info.instagram}
            />
            <div className="border border-gold/20 bg-navy-surface p-7">
              <div className="text-xs uppercase tracking-[0.25em] text-gold/80 mb-4">
                {t.contact.info.name}
              </div>
              <ul className="space-y-3 text-cream/80 text-sm">
                <li className="flex items-center gap-3">
                  <Mail size={16} className="text-gold/70" /> {t.contact.info.owner}
                </li>
                <li className="flex items-center gap-3">
                  <MapPin size={16} className="text-gold/70" /> {t.contact.info.location}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const inputCls =
  "w-full bg-navy-surface border border-gold/15 px-4 py-3 text-cream placeholder:text-cream-muted/50 focus:outline-none focus:border-gold/60 transition-colors font-light text-base";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.25em] text-gold/80 mb-2 font-medium">
      {label} {required && <span className="text-gold">*</span>}
    </span>
    {children}
  </label>
);

const DirectLink = ({ href, icon, label, value, testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="group flex items-center justify-between gap-4 border border-gold/20 bg-navy-surface/60 hover:border-gold/60 hover:bg-navy-surface px-6 py-5 transition-all duration-300"
  >
    <div className="flex items-center gap-4">
      <span className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-navy transition-colors">
        {icon}
      </span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-cream-muted/70">{label}</div>
        <div className="text-cream font-medium tracking-wide">{value}</div>
      </div>
    </div>
    <span className="text-gold/40 group-hover:text-gold transition-colors text-lg">→</span>
  </a>
);

export const SectionLabel = ({ eyebrow }) => (
  <div className="flex items-center gap-4 mb-5">
    <span className="w-10 h-px bg-gold/60" />
    <span className="text-[11px] uppercase tracking-[0.35em] text-gold font-medium">{eyebrow}</span>
  </div>
);

export default ContactSection;
